import React, { useEffect, useRef } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  AutoComplete,
  Divider,
  Typography,
  Select,
  DatePicker,
  ConfigProvider,
  message,
  InputNumber,
} from "antd";
import {
  optionsCategory,
  optionsProfile,
  optionsPlan,
} from "../constants/bannerOptions";
import {
  formatDate,
  normalizeBanner,
  exportBannerJson,
  importBannerJson,
} from "../utils/bannerHelpers";
import ptBR from "antd/es/locale/pt_BR";
import classes from "../AddBanner.module.scss";
import ButtonActions from "../../../../../Components/Buttons/ButtonActions/ButtonActions";
import Header from "../../../../../Components/Header/Header";
import dayjs from "dayjs";

const AddBannerPage1 = ({
  nextPage,
  handleCancel,
  initialValues,
  bannerData,
}) => {
  const [form] = Form.useForm();

  // estilos dos labels dos campos
  const labelStyle = {
    color: "#3C3C3C",
    fontSize: "16px",
    fontWeight: "500",
  };

  // estilo padrão dos inputs
  const inputStyle = { width: "80%" };

  // verifica se está em modo edição
  const isEditingBanner = !!bannerData?.banner_id;

  // referência do input de arquivo escondido
  const fileInputRef = useRef(null);

  const handleSubmit = async () => {
    try {
      // valida os campos do formulário
      const values = await form.validateFields();

      // formata as datas antes de enviar
      const banner = formatDate(values);

      // envia os dados para a próxima página
      nextPage(banner);
    } catch (error) {
      console.log("Erro nos campos:", error);
    }
  };

  const handleExportJson = () => {
    try {
      // exporta os dados do formulário em arquivo JSON
      exportBannerJson(form, bannerData);
    } catch (error) {
      console.log(error);
      message.error("Erro ao exportar");
    }
  };

  const handleImportClick = () => {
    // dispara o clique no input escondido
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    // pega o arquivo selecionado no input
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // limpa para permitir reimportar o mesmo arquivo
    event.target.value = null;

    try {
      // importa os dados do JSON e preenche o formulário
      importBannerJson(file, form);

      message.success("Informações importadas com sucesso");
    } catch (error) {
      console.log(error);
      message.error("Arquivo inválido ou formato JSON incorreto");
    }
  };

  useEffect(() => {
    if (!bannerData) return;

    // quando vier dados do backend, normaliza e preenche o form
    form.setFieldsValue(normalizeBanner(bannerData));
  }, [bannerData]);

  return (
    <div>
      <Header text={isEditingBanner ? "Editar Banner" : "Cadastrar Banner"} />
      <div style={{ marginBottom: 16, display: "flex", gap: "10px" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <ButtonActions
          variant="outline"
          onClick={handleImportClick}
          text="Importar Informações"
        />

        <ButtonActions
          variant="outline"
          onClick={handleExportJson}
          style={{ marginRight: 8 }}
          text="Exportar Informações"
        />
      </div>

      <Form
        form={form}
        style={{ maxWidth: 1200 }}
        size="large"
        layout="vertical"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Nome do Banner*</span>}
              name="banner_name"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                { min: 3, message: "Mínimo de 3 caracteres" },
                { max: 100, message: "Máximo de 100 caracteres" },
                { pattern: /\S/, message: "Não pode conter apenas espaços" },
              ]}
            >
              <Input
                style={inputStyle}
                placeholder="Nome do Banner"
                maxLength={100}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Categoria*</span>}
              name="category"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                { min: 3, message: "Mínimo de 3 caracteres" },
                { max: 30, message: "Máximo de 30 caracteres" },
                { pattern: /\S/, message: "Não pode conter apenas espaços" },
              ]}
            >
              <AutoComplete
                placeholder="Categoria "
                tokenSeparators={[","]}
                options={optionsCategory}
                style={inputStyle}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Link de destino*</span>}
              name="link_destination"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                { max: 300, message: "Máximo de 500 caracteres" },
                { pattern: /\S/, message: "Não pode conter apenas espaços" },
                {
                  validator: (_, value) => {
                    if (!value || !value.trim()) return Promise.resolve();
                    const isInternalPath = /^\/\S*$/.test(value);
                    const isExternalUrl = /^https?:\/\/.+/.test(value);
                    if (isInternalPath || isExternalUrl)
                      return Promise.resolve();
                    return Promise.reject(
                      new Error(
                        "Informe um link iniciado com http:// ou https://, ou um caminho interno iniciado com /"
                      )
                    );
                  },
                },
              ]}
            >
              <Input
                style={inputStyle}
                placeholder="https://exemplo.com/pagina ou /jogos?atividade=escalada"
                maxLength={300}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Prioridade*</span>}
              name="priority"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                {
                  type: "number",
                  min: 1,
                  max: 10,
                  message: "A prioridade deve ser entre 1 e 10",
                },
              ]}
            >
              <InputNumber
                placeholder="Prioridade"
                style={inputStyle}
                min={1}
                max={10}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ borderWidth: "2px", marginBottom: "50px" }} />

        <Typography.Text className={classes.Text}>
          Defina quais usuários poderão visualizar este banner na plataforma
        </Typography.Text>

        <Row gutter={[16, 16]} style={{ marginTop: "40px" }}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Profile*</span>}
              name="profile_can_view"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                {
                  validator: (_, value) => {
                    if (value.some((item) => item.trim() === "")) {
                      return Promise.reject("Não pode conter valores vazios");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                placeholder="Selecione..."
                mode="tags"
                tokenSeparators={[","]}
                options={optionsProfile}
                style={inputStyle}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Plano*</span>}
              name="plan_name_can_view"
              rules={[
                {
                  required: true,
                  message: "Este campo não pode permanecer vazio",
                },
                {
                  validator: (_, value) => {
                    if (value.some((item) => item.trim() === "")) {
                      return Promise.reject("Não pode conter valores vazios");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                placeholder="Selecione..."
                mode="tags"
                tokenSeparators={[","]}
                options={optionsPlan}
                style={inputStyle}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ borderWidth: "2px", marginBottom: "50px" }} />

        <Typography.Text className={classes.Text}>
          Escolha a data de início e a data de término da exibição do banner
        </Typography.Text>

        <Row gutter={[16, 16]} style={{ marginTop: "40px" }}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Data de Início*</span>}
              name="start_date"
              valuePropName="value"
              rules={[
                {
                  required: true,
                  message: "Preenchimento inválido. Confira os dados.",
                },
              ]}
            >
              <DatePicker
                placeholder="00/00/0000"
                format="DD/MM/YYYY"
                style={inputStyle}
                disabledDate={(current) =>
                  current && current < new Date().setHours(0, 0, 0, 0)
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={<span style={labelStyle}>Data de Término</span>}
              name="end_date"
              valuePropName="value"
              rules={[
                {
                  required: true,
                  message: "Preenchimento inválido. Confira os dados.",
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    const today = dayjs().startOf("day");

                    if (value.startOf("day").isBefore(today)) {
                      return Promise.reject(
                        "A data não pode ser anterior a hoje",
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                placeholder="00/00/0000"
                format="DD/MM/YYYY"
                style={inputStyle}
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item wrapperCol={{ offset: 4, span: 16 }}>
          <div className={classes.buttonContainer}>
            <ButtonActions
              variant="outline"
              onClick={handleCancel}
              text="Cancelar"
            />

            <ButtonActions
              variant="primary"
              onClick={handleSubmit}
              text="Continuar"
            />
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddBannerPage1;
